import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRoom } from '../components/RoomContext';
import {
  Users,
  Plus,
  Loader,
  AlertCircle,
  UserPlus,
  Share2,
  LogIn
} from 'lucide-react';
import { nanoid } from 'nanoid';

const apiUrl = process.env.NODE_ENV === 'production'
    ? ''
    : 'http://localhost:3001';

export default function RoomEntry() {
  const [createName, setCreateName] = useState('');
  const [joinRoomId, setJoinRoomId] = useState('');
  const [joinName, setJoinName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [joinError, setJoinError] = useState('');
  const { joinRoom } = useRoom();
  const navigate = useNavigate();
  const location = useLocation();

  // Prefill joinRoomId if coming from a shared link
  const [autoJoin, setAutoJoin] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const room = params.get('room');
    if (room) {
      setJoinRoomId(room);
      setAutoJoin(true);
    }
  }, [location.search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) {
      setCreateError('Voer je naam in');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      // Generate room code (YYYYMMDD-nanoid)
      const code = `${new Date().toISOString().slice(0,10).replace(/-/g, '')}-${nanoid(6)}`;
      const res = await fetch(`${apiUrl}/api/room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: code, createdBy: createName.trim() })
      });
      if (!res.ok) throw new Error('Aanmaken mislukt');
      const room = await res.json();
      joinRoom(room, createName.trim());
      localStorage.setItem(`room_${room.id}_user`, JSON.stringify({ username: createName.trim() }));
      navigate(`/room/${room.id}`);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!joinName.trim()) {
      setJoinError('Voer je naam in');
      return;
    }
    if (!joinRoomId.trim()) {
      setJoinError('Voer de groepscode in');
      return;
    }
    setJoinLoading(true);
    setJoinError('');
    try {
      const res = await fetch(`${apiUrl}/api/room/${joinRoomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: joinName.trim() })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.error || 'Deelnemen mislukt');
      }
      const room = await res.json();
      joinRoom(room, joinName.trim());
      navigate(`/room/${room.id}`);
    } catch (err) {
      setJoinError(err.message);
    } finally {
      setJoinLoading(false);
    }
  };

  return (
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-md mx-auto space-y-6">
          {/* Create Room Card */}
          {!autoJoin && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-green-100 rounded-full">
                      <Users className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">
                    Nieuwe gezamenlijke bestelling
                  </h1>
                  <p className="text-gray-600">
                    Maak een nieuwe groepje aan om samen te bestellen met vrienden of collega’s
                  </p>
                </div>

                {createError && (
                    <div className="mb-4 p-3 bg-red-100 border-2 border-red-600 rounded-md text-red-800 flex items-center gap-2">
                      <AlertCircle size={16} />
                      {createError}
                    </div>
                )}

                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label htmlFor="createName" className="block text-sm font-medium text-gray-700 mb-1">
                      Jouw naam
                    </label>
                    <input
                        id="createName"
                        type="text"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        placeholder="Voer je naam in..."
                        className="w-full px-4 py-3 border-2 border-green-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200"
                        disabled={createLoading}
                    />
                  </div>

                  <button
                      type="submit"
                      disabled={createLoading}
                      className="w-full px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createLoading ? (
                        <>
                          <Loader className="animate-spin" size={18} />
                          Room aanmaken...
                        </>
                    ) : (
                        <>
                          <Plus size={18} />
                          Maak groepje aan
                        </>
                    )}
                  </button>
                </form>

                {/* How It Works */}
                <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <Share2 className="text-blue-600" size={16} />
                    <h3 className="font-medium text-blue-800">Hoe werkt het?</h3>
                  </div>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Jij maakt een groepje aan met je naam</li>
                    <li>• Je krijgt een unieke link om te delen</li>
                    <li>• Anderen kunnen deelnemen met hun naam</li>
                    <li>• Iedereen kan zijn eigen bestelling toevoegen</li>
                    <li>• Jullie zien samen het totaaloverzicht</li>
                  </ul>
                </div>
              </div>
          )}
        </div>
      </div>
  );
}
